import type {Component, Editor} from 'grapesjs';
import type {ElementData, ImageData, StickerData, VideoData} from '@Libraries/add-media-library';
import {MultiMediaUploadSource} from '@Libraries/user-media-library';
import {GA4EventName} from '@Libraries/ga-events';
import {UserImageSource} from '@Libraries/user-image-library';
import {UserVideoSource} from '@Libraries/user-video-library';
import {StockSources} from '@Libraries/pmw-stock-media-library';
import {convertComponentIfNeeded} from '@Libraries/email-campaigns-library';
import {noop} from '@Utils/general.util';
import {openMessageModal} from '@Components/modals/message-modal';
import {openErrorModal} from '@Modals/error-modal';
import {openMediaTypeModal} from '@Modals/media-type-modal/media-type-modal';
import {MediaType} from '@Panels/media-type-panel/media-type-panel';
import {openStockMediaModal} from '@Modals/stock-modal';
import {StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import {openUserMediaModal} from '@Modals/user-media-modal';
import {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import type {EmbedVideosEmailmakerInputModalProps} from '@Modals/embed-videos-emailmaker-input-modal';
import {openEmbedVideosEmailmakerModal} from '@Modals/embed-videos-emailmaker-input-modal';
import {EMAIL_MAKER_BLOCKS, EMAIL_MAKER_COMPONENT_TYPES, MAX_VIDEOS_ALLOWED} from '@EmailMakerReact/email-maker-react.constants';
import {DATA_URL_ATTRIBUTE, EMBEDDED_VIDEO_ATTRIBUTE, removeComponentAnchor} from '@EmailMakerReact/lib/email-maker-anchor-link-library';
import {getVideoCount, setEmbeddedVideoSrc, setImageLoading, setImageSrc} from '@EmailMakerReact/lib/email-maker-media-library';
import {trackGaEventForComponent} from '@EmailMakerReact/lib/email-maker-misc-library';
import {saveEmailTemplateToCache} from '@EmailMakerReact/lib/email-maker-storage-library';

/**
 * Media picker modals for the React email maker.
 * Selected media is applied through the media library, which owns the setImageSrc /
 * setEmbeddedVideoSrc pipeline and the endpoint contracts.
 */

/** Legacy stock-modal panel id for the email editor. */
const STOCK_MEDIA_PANEL_ID = 'STOCK_MEDIA_VIA_EMAIL_EDITOR';

export interface OpenMediaPickerModalOptions<ItemType> {
  onAddSelected: (items: ItemType[]) => void;
  onStartAddSelected: () => void;
}

/** Media-type picker, falling back to image-only selection when the video limit is hit. */
export function openMediaModal(editor: Editor, options: OpenMediaPickerModalOptions<ImageData>): void {
  const videosAllowedCount = MAX_VIDEOS_ALLOWED - getVideoCount(editor);
  const sharedModalProps = {
    onStartAddSelected: options.onStartAddSelected,
    onAddSelected: options.onAddSelected,
    onAddBrandAsset: (): void => {
      void onAddBrandAsset(editor);
    },
    maxSelectionNumber: 1,
    uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
    videoQualityFiltersDisabled: true,
    hideRecordingOptions: true,
  };

  if (videosAllowedCount <= 0) {
    openMessageModal({
      title: window.i18next.t('pmwjs_video_limit'),
      text: window.i18next.t('pmwjs_video_limit_message', {num: MAX_VIDEOS_ALLOWED}),
      onClose: (): void => {
        openMediaTypeModal({mediaType: MediaType.IMAGE, ...sharedModalProps});
      },
    });
    return;
  }

  openMediaTypeModal(sharedModalProps);
}

/** Stock sticker picker. */
export function openStickerModal(options: OpenMediaPickerModalOptions<StickerData>): void {
  openStockMediaModal({
    onStartAddSelected: options.onStartAddSelected,
    tabsToShow: [StockPanelTabs.STICKERS],
    onAddSelected: options.onAddSelected,
    maxSelectionNumber: 1,
    defaultSearchTab: StockPanelTabs.STICKERS,
    hideOnClose: false,
    panelId: STOCK_MEDIA_PANEL_ID,
  });
}

/** Stock icons picker. */
export function openIconsModal(options: OpenMediaPickerModalOptions<ElementData>): void {
  openStockMediaModal({
    onStartAddSelected: options.onStartAddSelected,
    tabsToShow: [StockPanelTabs.ICONS],
    onAddSelected: options.onAddSelected,
    maxSelectionNumber: 1,
    defaultSearchTab: StockPanelTabs.ICONS,
    hideOnClose: false,
    panelId: STOCK_MEDIA_PANEL_ID,
  });
}

/** Persists the template (so the brand asset is referenced) before running the callback. */
export async function onAddBrandAsset(editor: Editor, cb?: VoidFunction): Promise<void> {
  await saveEmailTemplateToCache(editor);
  cb?.();
}

/** Image source picker for the component (or selection). */
export function openAddImageOptionSelector(editor: Editor, component?: Component): void {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  openMediaTypeModal({
    onStartAddSelected: createImageLoadingStarter(editor, target),
    onAddSelected: (images: ImageData[]): void => {
      handleStockMediaImageSelection(editor, images, target);
    },
    maxSelectionNumber: 1,
    mediaType: MediaType.IMAGE,
    title: window.i18next.t('pmwjs_select_an_option'),
    blacklistedSources: [StockSources.PMW_STOCK_IMAGE],
    hideRecordingOptions: true,
    uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
  });
}

/** My-uploads image picker for the component (or selection). */
export function openMyUploadsImage(editor: Editor, component?: Component): void {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  openUserMediaModal({
    onStartAddSelected: createImageLoadingStarter(editor, target),
    onAddSelected: (images: ImageData[]): void => {
      handleStockMediaImageSelection(editor, images, target);
    },
    maxSelectionNumber: 1,
    defaultTab: UploadsMediaType.IMAGE,
    mediaTypesToShow: [UploadsMediaType.IMAGE],
    uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
    uploadAcceptedMimetype: 'image/*',
    hideFooterSecondaryActions: true,
  });
}

/** Stock photos picker for the component (or selection). */
export function openExploreMediaImage(editor: Editor, component?: Component): void {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  openStockMediaModal({
    onStartAddSelected: createImageLoadingStarter(editor, target),
    onAddSelected: (images: ImageData[]): void => {
      handleStockMediaImageSelection(editor, images, target);
    },
    maxSelectionNumber: 1,
    tabsToShow: [StockPanelTabs.PHOTOS],
    defaultSearchTab: StockPanelTabs.PHOTOS,
    populateRecentlyAddedOnItemAdd: true,
    showTextToImageTool: true,
  });
}

/** Text-to-image picker for the component (or selection). */
export function openAiImages(editor: Editor, component?: Component): void {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  openStockMediaModal({
    onStartAddSelected: createImageLoadingStarter(editor, target),
    onAddSelected: (images: ImageData[]): void => {
      handleStockMediaImageSelection(editor, images, target);
    },
    maxSelectionNumber: 1,
    tabsToShow: [StockPanelTabs.TEXT_TO_IMAGE],
    defaultSearchTab: StockPanelTabs.TEXT_TO_IMAGE,
    populateRecentlyAddedOnItemAdd: true,
    showTextToImageTool: true,
  });
}

/** Video source picker (including embed-video entry) for the component (or selection). */
export function openAddVideoOptionSelector(editor: Editor, component?: Component): void {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  const videoModalProps = getEmbedVideoModalProps(editor, target);

  openMediaTypeModal({
    title: window.i18next.t('pmwjs_select_an_option'),
    videoQualityFiltersDisabled: true,
    mediaType: MediaType.VIDEO,
    onAddSelected: (videos: VideoData[]): void => {
      handleStockMediaVideoSelection(editor, videos, target);
    },
    hideRecordingOptions: true,
    maxSelectionNumber: 1,
    uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
    showEmbedVideo: true,
    onAddEmbeddedVideo: videoModalProps.onAddVideo,
    onVideoUploadRequest: videoModalProps.onVideoUploadRequest,
    onVideoUploadError: videoModalProps.onVideoUploadError,
    onBack: (): void => {
      openEmbedVideo(editor);
    },
  });
}

/** My-uploads video picker for the current selection. */
export function openMyUploadsVideo(editor: Editor): void {
  const component = editor.getSelected();
  if (!component) {
    return;
  }

  openUserMediaModal({
    hideRecordingOptions: true,
    uploadAcceptedMimetype: 'video/*,image/gif',
    onStartAddSelected: createImageLoadingStarter(editor, component),
    onAddSelected: (videos: VideoData[]): void => {
      handleStockMediaVideoSelection(editor, videos, component);
    },
    maxSelectionNumber: 1,
    defaultTab: UploadsMediaType.VIDEO,
    mediaTypesToShow: [UploadsMediaType.VIDEO],
    uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
    videoQualityFiltersDisabled: true,
  });
}

/** Stock videos picker for the current selection. */
export function openExploreMediaVideo(editor: Editor): void {
  const component = editor.getSelected();
  if (!component) {
    return;
  }

  openStockMediaModal({
    onStartAddSelected: createImageLoadingStarter(editor, component),
    onAddSelected: (videos: VideoData[]): void => {
      handleStockMediaVideoSelection(editor, videos, component);
    },
    tabsToShow: [StockPanelTabs.VIDEOS],
    defaultSearchTab: StockPanelTabs.VIDEOS,
    maxSelectionNumber: 1,
    populateRecentlyAddedOnItemAdd: true,
    videoQualityFiltersDisabled: true,
  });
}

/** Embed-video URL modal for the current selection. */
export function openEmbedVideo(editor: Editor): void {
  const component = editor.getSelected();
  if (!component) {
    return;
  }

  openEmbedVideosEmailmakerModal(getEmbedVideoModalProps(editor, component));
}

/** Applies the picked image (converting a design or icon component back to an image first). */
function handleStockMediaImageSelection(editor: Editor, images: ImageData[], component: Component): void {
  if (images.length === 0) {
    return;
  }

  const imageComponent = convertComponentToImageIfNeeded(editor, component);

  if (images[0].source === UserImageSource.EMAIL_UPLOAD || images[0].source === UserImageSource.UPLOAD) {
    trackGaEventForComponent(editor, GA4EventName.EMAILMAKER_UPLOAD_MEDIA, imageComponent);
  }

  void setImageSrc(editor, {
    component: imageComponent,
    source: images[0].source,
    hashedFilename: images[0].hashedFilename,
  });
}

/**
 * Replacing media with an image turns a design or icon component into a plain image component,
 * so its serialized media type (which backend preset creation is keyed off) matches the new content.
 */
function convertComponentToImageIfNeeded(editor: Editor, component: Component): Component {
  const convertedComponent = convertComponentIfNeeded(editor, component, EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN, EMAIL_MAKER_BLOCKS.ID_EXPLORE_MEDIA);
  return convertComponentIfNeeded(editor, convertedComponent, EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_ICON, EMAIL_MAKER_BLOCKS.ID_EXPLORE_MEDIA);
}

/** Applies the picked video, clearing any embed-video link state. */
function handleStockMediaVideoSelection(editor: Editor, videos: VideoData[], component: Component): void {
  if (videos.length === 0) {
    return;
  }

  component.removeAttributes([DATA_URL_ATTRIBUTE]);
  component.addAttributes({[EMBEDDED_VIDEO_ATTRIBUTE]: 0});

  if (videos[0].source === UserVideoSource.EMAIL_UPLOAD || videos[0].source === UserVideoSource.UPLOAD) {
    trackGaEventForComponent(editor, GA4EventName.EMAILMAKER_UPLOAD_MEDIA, component);
  }

  removeComponentAnchor(editor, component);

  void setImageSrc(editor, {
    component,
    source: videos[0].source,
    hashedFilename: videos[0].hashedFilename,
  });
}

/** Shared embed-video modal callbacks for a component. */
function getEmbedVideoModalProps(editor: Editor, component: Component): EmbedVideosEmailmakerInputModalProps {
  return {
    onAddVideo: (videoUrl: string, thumbnailUrl: string): void => {
      setEmbeddedVideoSrc(editor, {component, thumbnailURL: thumbnailUrl, videoURL: videoUrl});
    },
    onVideoUploadRequest: createImageLoadingStarter(editor, component),
    onVideoUploadError: (errorMessage: string): void => {
      openErrorModal({message: errorMessage});
      setImageLoading(editor, {component, loading: false});
    },
    onBack: noop,
  };
}

function createImageLoadingStarter(editor: Editor, component: Component): () => void {
  return (): void => {
    setImageLoading(editor, {component, loading: true, text: window.i18next.t('pmwjs_loading')});
  };
}
